import React from "react";
import { ukDayYear as fmt } from "@/lib/time";
import { FileText, Eye, Download, Paperclip } from "lucide-react";
import { FileThumb } from "@/components/portal/FileThumb";
import { useLightbox, canPreview } from "@/components/portal/FileLightbox";

const prettySize = (n) => {
  n = Number(n) || 0;
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
};

/**
 * CaseFiles — everything sitting on this case, wherever it came from: the original
 * application, a client upload against a document request, or a file dropped into chat.
 * Anything readable opens in place; the rest downloads.
 */
export const CaseFiles = ({ files = [], testPrefix = "case-file" }) => {
  const { openOrDownload, lightbox } = useLightbox();

  if (files.length === 0) {
    return (
      <div className="flex items-start gap-2 text-[12.5px] text-slate-500" data-testid={`${testPrefix}-empty`}>
        <Paperclip className="size-3.5 mt-0.5 shrink-0 text-slate-400" />
        Nothing on file yet. Anything the client uploads, or a partner drops into chat against this case,
        lands here automatically.
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {files.map((f) => {
        const readable = canPreview(f.filename, f.content_type);
        return (
          <button key={f.id} onClick={() => openOrDownload(f)}
                  className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-left hover:border-[#0055FF]/40 transition-colors"
                  data-testid={`${testPrefix}-${f.id}`}>
            <FileThumb fileId={f.id} alt={f.filename} size={42} hasThumb={f.has_thumb}
                       fallback={<FileText className="size-4 text-slate-400 shrink-0" />} />
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-medium text-slate-900 truncate">{f.filename}</span>
              <span className="block text-[11px] text-slate-500">
                {prettySize(f.size)}{f.uploaded_at ? ` · added ${fmt(f.uploaded_at)}` : ""}
              </span>
            </span>
            {readable
              ? <Eye className="size-3.5 text-slate-400 shrink-0" />
              : <Download className="size-3.5 text-slate-400 shrink-0" />}
          </button>
        );
      })}
      {lightbox}
    </div>
  );
};
